import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useUrlSearchParams } from 'use-url-search-params';
import { Table, Row, Col, Spinner, Button, Input, Collapse } from 'reactstrap';
import { DateRangePicker } from 'rsuite';
import moment from 'moment';
import DHPagination from '../../../components/Pagination';
import {
  fetchDriverAnalysisFilterOptions,
  fetchDriverAnalysisList,
} from '../actions/action';
import DriverAnalysisSummary from './DriverAnalysisSummary';
import DriverAnalysisDetailsOffcanvas from './DriverAnalysisDetailsOffcanvas';
import DeliveryReviewDetailsOffcanvas from '../../Reviews/components/ReviewDetailsOffcanvas';
import ImagePreviewModal from './ImagePreviewModal';

/* Stars are always green here, regardless of the rating value. */
const STAR_COLOR = '#28a745';

const renderStars = (rating) => {
  const value = Math.round(Number(rating) || 0);
  return (
    <span className="d-inline-flex align-items-center" title={`${rating} / 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`mdi ${star <= value ? 'mdi-star' : 'mdi-star-outline'}`}
          style={{ color: STAR_COLOR }}
        ></i>
      ))}
    </span>
  );
};

/* Statuses are stored as sent by FarEye ("delivery_success"). Shown with the
   underscores dropped; text-capitalize handles the casing in CSS. */
const deliveryStatusBadge = (status) => {
  const value = String(status || '').trim();
  if (!value) return '-';

  // Background carries the outcome; the label itself stays black so it reads
  // as text rather than as another coloured count badge.
  const key = value.toLowerCase();
  let background = 'bg-soft-secondary';
  if (key.includes('success')) {
    background = 'bg-soft-success';
  } else if (key.includes('partial')) {
    background = 'bg-soft-warning';
  } else if (key.includes('fail')) {
    background = 'bg-soft-danger';
  }

  return (
    <span className={`badge ${background} text-dark text-capitalize`}>
      {value.replace(/_/g, ' ')}
    </span>
  );
};

/* lifestyle_images is a JSON column - the driver returns it parsed, but
   tolerate a raw string so the badge still opens if it is stored as text. */
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

/* Reshape the delivery_* columns the listing joins in into the row shape the
   Delivery Feedback drawer expects. */
const toDeliveryReview = (row) => ({
  id: row?.review_id,
  reference_no: row?.delivery_reference_no,
  shipment_reference: row?.delivery_shipment_reference,
  rating: row?.delivery_rating,
  review: row?.delivery_review,
  feedback_data: row?.delivery_feedback_data,
  type: row?.delivery_type,
  email: row?.delivery_email,
  customer_id: row?.delivery_customer_id,
  country: row?.delivery_country,
  source: row?.delivery_source,
  order_created_at: row?.delivery_order_created_at,
  delivered_at: row?.delivery_delivered_at,
  created_at: row?.delivery_created_at,
});

const DriverAnalysisList = (props) => {
  const { refreshList } = props;
  const dispatch = useDispatch();

  const [paginationValue, setPaginationValue] = useUrlSearchParams({ page: 0 });
  const [limitValue, setLimitValue] = useUrlSearchParams({ limit: 10 });
  const [searchParams, setSearchParams] = useUrlSearchParams({ search: '' });

  const [searchValue, setSearchValue] = useState(searchParams.search || '');
  const [dateRangeValue, setDateRangeValue] = useState(null);
  const [dateFilter, setDateFilter] = useState({ dateFrom: '', dateTo: '' });
  /* Transaction date is when the delivery was captured (created_at), which can
     be a later day than the delivery itself - so it filters separately. */
  const [createdRangeValue, setCreatedRangeValue] = useState(null);
  const [createdFilter, setCreatedFilter] = useState({
    createdFrom: '',
    createdTo: '',
  });
  const [isDataLoading, setDataLoading] = useState(false);
  const [listData, setListData] = useState([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [driverFilter, setDriverFilter] = useState('');
  const [dispatchFilter, setDispatchFilter] = useState('');
  /* '' = all, 'rated' = latest review rated above 0, 'non_rated' = no review
     or a rating of 0. */
  const [ratingFilter, setRatingFilter] = useState('');
  const [filterOptions, setFilterOptions] = useState({
    drivers: [],
    dispatchLocations: [],
  });
  /* The summary is hidden until asked for. hasLoadedSummary keeps it mounted
     after the first open, so reopening slides rather than refetching. */
  const [showSummary, setShowSummary] = useState(false);
  const [hasLoadedSummary, setHasLoadedSummary] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [preview, setPreview] = useState(null);

  /* The filter row carries six controls plus three buttons. On desktop the row
     does not wrap, so the controls share the line and shrink together instead
     of the buttons dropping to a row of their own; below that it wraps. */
  const filterCell = { flex: '1 1 150px', minWidth: 0 };

  /* Proof-of-delivery photos plus the signature as one browsable set, so the
     arrows in the lightbox move between them whichever badge was clicked. */
  const podImagesOf = (row) => {
    const soNo = row?.so_no || '';
    return [
      ...toArray(row?.lifestyle_images)
        .map((image, index) => ({
          url: typeof image === 'string' ? image : image?.url,
          label: `Proof of Delivery ${index + 1} - ${soNo}`,
        }))
        .filter((image) => !!image.url),
      ...(row?.signature_url
        ? [
            {
              url: row.signature_url,
              label: `Customer Signature - ${soNo}`,
            },
          ]
        : []),
    ];
  };

  const openPodImages = (row) =>
    setPreview({ images: podImagesOf(row), index: 0 });

  const currentCountry = localStorage.getItem('WEBSITE_COUNTRY');

  const loadList = useCallback(async (params) => {
    try {
      setDataLoading(true);
      const response = await fetchDriverAnalysisList({ params, dispatch });
      setListData(response?.data || []);
      setRecordsTotal(Number(response?.recordsTotal || 0));
    } catch (error) {
      console.error('Error fetching driver analysis:', error);
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => {
      loadList({
        start: Number(paginationValue.page * limitValue.limit),
        limit: Number(limitValue.limit),
        search: searchParams?.search || '',
        dateFrom: dateFilter.dateFrom,
        dateTo: dateFilter.dateTo,
        createdFrom: createdFilter.createdFrom,
        createdTo: createdFilter.createdTo,
        country: currentCountry,
        driverName: driverFilter,
        dispatchLocation: dispatchFilter,
        rating: ratingFilter,
      });
    }, 300);

    return () => clearTimeout(delay);
  }, [
    paginationValue.page,
    limitValue,
    searchParams,
    dateFilter,
    createdFilter,
    driverFilter,
    dispatchFilter,
    ratingFilter,
    refreshList,
  ]);

  /* Dropdown values, loaded once per country - they come from the captured
     rows, so the options can only be values that appear in the listing. */
  useEffect(() => {
    const loadFilterOptions = async () => {
      const response = await fetchDriverAnalysisFilterOptions({
        params: { country: currentCountry },
        dispatch,
      });
      setFilterOptions({
        drivers: response?.drivers || [],
        dispatchLocations: response?.dispatchLocations || [],
      });
    };
    loadFilterOptions();
  }, [currentCountry]);

  const handleDateChange = (dates) => {
    if (dates?.[0] && dates?.[1]) {
      setDateFilter({
        dateFrom: moment(dates[0]).format('YYYY-MM-DD'),
        dateTo: moment(dates[1]).format('YYYY-MM-DD'),
      });
      setDateRangeValue(dates);
    }
  };

  const handleDateClose = () => {
    setDateFilter({ dateFrom: '', dateTo: '' });
    setDateRangeValue(null);
  };

  const handleCreatedDateChange = (dates) => {
    if (dates?.[0] && dates?.[1]) {
      setCreatedFilter({
        createdFrom: moment(dates[0]).format('YYYY-MM-DD'),
        createdTo: moment(dates[1]).format('YYYY-MM-DD'),
      });
      setCreatedRangeValue(dates);
      setPaginationValue({ page: 0 });
    }
  };

  const handleCreatedDateClose = () => {
    setCreatedFilter({ createdFrom: '', createdTo: '' });
    setCreatedRangeValue(null);
  };

  const handleSearchButtonClick = () => {
    setSearchParams({ search: searchValue });
    setPaginationValue({ page: 0 });
  };

  const handleResetSearch = () => {
    setSearchValue('');
    setSearchParams({ search: '' });
    setPaginationValue({ page: 0 });
    setDriverFilter('');
    setDispatchFilter('');
    setRatingFilter('');
    handleDateClose();
    handleCreatedDateClose();
  };

  const disableFutureDates = (date) => date.getTime() > new Date().getTime();

  /* The charts read the listing's filters - minus paging, which must not change
     what the summary describes. */
  const summaryParams = {
    search: searchParams?.search || '',
    dateFrom: dateFilter.dateFrom,
    dateTo: dateFilter.dateTo,
    createdFrom: createdFilter.createdFrom,
    createdTo: createdFilter.createdTo,
    country: currentCountry,
    driverName: driverFilter,
    dispatchLocation: dispatchFilter,
    rating: ratingFilter,
  };

  return (
    <>
      <Row>
        <Col xl={12}>
          <div
            className="p-3 bg-soft-light border-1 border-light"
            style={{ borderStyle: 'dashed' }}
          >
            <Row>
              <Col
                md={12}
                className="d-flex flex-row flex-wrap flex-xl-nowrap align-items-center gap-2"
              >
                <div style={filterCell}>
                  <Input
                    type="text"
                    placeholder="Search SO / Driver / Customer"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSearchButtonClick();
                    }}
                  />
                </div>
                <div style={filterCell}>
                  <DateRangePicker
                    size="md"
                    className="w-100"
                    placement="auto"
                    placeholder="Delivery Date Range"
                    showOneCalendar={false}
                    disabledDate={disableFutureDates}
                    onChange={handleDateChange}
                    onClean={handleDateClose}
                    value={dateRangeValue}
                  />
                </div>
                <div style={filterCell}>
                  <DateRangePicker
                    size="md"
                    className="w-100"
                    placement="auto"
                    placeholder="Transaction Date Range"
                    showOneCalendar={false}
                    disabledDate={disableFutureDates}
                    onChange={handleCreatedDateChange}
                    onClean={handleCreatedDateClose}
                    value={createdRangeValue}
                  />
                </div>
                <div style={filterCell}>
                  <Input
                    type="select"
                    value={driverFilter}
                    onChange={(e) => {
                      setDriverFilter(e.target.value);
                      setPaginationValue({ page: 0 });
                    }}
                  >
                    <option value="">All Drivers</option>
                    {filterOptions.drivers.map((driver) => (
                      <option
                        key={`${driver?.driver_emp_id}-${driver?.driver_name}`}
                        value={driver?.driver_name}
                      >
                        {driver?.driver_name}
                        {driver?.driver_emp_id
                          ? ` (${driver.driver_emp_id})`
                          : ''}
                      </option>
                    ))}
                  </Input>
                </div>
                <div style={filterCell}>
                  <Input
                    type="select"
                    value={dispatchFilter}
                    onChange={(e) => {
                      setDispatchFilter(e.target.value);
                      setPaginationValue({ page: 0 });
                    }}
                  >
                    <option value="">All Dispatch Locations</option>
                    {filterOptions.dispatchLocations.map((location) => (
                      <option key={location} value={location}>
                        {location}
                      </option>
                    ))}
                  </Input>
                </div>
                <div style={filterCell}>
                  <Input
                    type="select"
                    value={ratingFilter}
                    onChange={(e) => {
                      setRatingFilter(e.target.value);
                      setPaginationValue({ page: 0 });
                    }}
                  >
                    <option value="">All Ratings</option>
                    <option value="rated">Rated</option>
                    <option value="non_rated">Non Rated</option>
                  </Input>
                </div>
                <div className="d-flex gap-2 flex-shrink-0">
                  <Button
                    color="secondary"
                    type="button"
                    onClick={handleSearchButtonClick}
                  >
                    <i className="bx bx-search-alt font-size-16 align-middle"></i>
                  </Button>
                  <Button
                    outline
                    color="danger"
                    type="button"
                    onClick={handleResetSearch}
                  >
                    <i className="bx bx-revision font-size-16 align-middle"></i>
                  </Button>
                  {/* Charts are off by default; the first press mounts them,
                      later presses just slide the panel. */}
                  <Button
                    outline={!showSummary}
                    color="dark"
                    type="button"
                    className="d-flex align-items-center gap-1 text-nowrap"
                    aria-expanded={showSummary}
                    onClick={() => {
                      setHasLoadedSummary(true);
                      setShowSummary((open) => !open);
                    }}
                  >
                    <i className="bx bx-bar-chart-alt-2 font-size-16 align-middle"></i>
                    {showSummary ? 'Hide analytics' : 'Analytics'}
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          {/* Charts read the same filters as the table, so the two always
              describe the same rows. Collapsed until the button is pressed. */}
          <Collapse isOpen={showSummary}>
            {hasLoadedSummary ? (
              <DriverAnalysisSummary params={summaryParams} />
            ) : null}
          </Collapse>

          <div className="dt-table">
            <div className="table-responsive">
              <Table className="OrderListTable ServiceOrderTable table mb-0">
                <thead>
                  <tr>
                    <th nowrap="nowrap">SO No</th>
                    <th nowrap="nowrap">Transaction Date</th>
                    <th nowrap="nowrap">Delivery Date</th>
                    <th nowrap="nowrap">Delivery Status</th>
                    <th nowrap="nowrap">Driver</th>
                    <th nowrap="nowrap">Customer</th>
                    <th nowrap="nowrap">Store</th>
                    <th nowrap="nowrap">Dispatch Location</th>
                    <th nowrap="nowrap">Items</th>
                    <th nowrap="nowrap">Qty (Ord / Del / Fix)</th>
                    <th nowrap="nowrap">Installation</th>
                    <th nowrap="nowrap">POD</th>
                    <th nowrap="nowrap">Action</th>
                  </tr>
                </thead>
                {isDataLoading ? (
                  <tbody>
                    <tr>
                      <td colSpan={13} className="text-center">
                        <Spinner className="ms-2" color="primary" />
                      </td>
                    </tr>
                  </tbody>
                ) : listData?.length > 0 ? (
                  <tbody>
                    {listData.map((row) => (
                      <tr key={row.id}>
                        <td>
                          {/* Badge and number stay on one line - flex keeps
                              them together however narrow the column gets. */}
                          <p className="fs-12 mb-0 d-flex align-items-center gap-1 text-nowrap">
                            {row?.so_country && (
                              <span className="badge bg-soft-secondary text-secondary text-uppercase">
                                {row.so_country}
                              </span>
                            )}
                            <b>{row?.so_no}</b>
                          </p>
                          {row?.erp_no && (
                            <p className="fs-10 mb-0 mt-1">
                              <b>ERP # :</b> {row.erp_no}
                            </p>
                          )}
                        </td>
                        {/* Transaction date is created_at - when the delivery
                            was captured, which can be later than the delivery
                            itself. */}
                        <td nowrap="nowrap">
                          {row?.created_at ? (
                            <>
                              <p className="fs-12 mb-0">
                                {moment(row.created_at).format('DD MMM, YYYY')}
                              </p>
                              <p className="fs-10 mb-0 mt-1 text-muted">
                                {moment(row.created_at).format('h:mm A')}
                              </p>
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                        {/* delivery_date - the column the Delivery Date filter
                            matches, so a filtered list shows the date it was
                            filtered on. */}
                        <td nowrap="nowrap">
                          <p className="fs-12 mb-0">
                            {row?.delivery_date
                              ? moment(row.delivery_date).format('DD MMM, YYYY')
                              : '-'}
                          </p>
                        </td>
                        <td nowrap="nowrap">
                          <div>{deliveryStatusBadge(row?.delivery_status)}</div>
                          {row?.delivery_failure_reason && (
                            <p className="fs-10 mb-0 mt-1 text-muted">
                              {row.delivery_failure_reason}
                            </p>
                          )}
                        </td>
                        <td>
                          <p className="fs-12 mb-0">{row?.driver_name || '-'}</p>
                          {row?.driver_emp_id && (
                            <p className="fs-10 mb-0 mt-1">
                              {row.driver_emp_id}
                            </p>
                          )}
                          {/* Customer's delivery rating - click to open the
                              feedback drawer. */}
                          {Number(row?.delivery_rating) > 0 && (
                            <span
                              role="button"
                              tabIndex={0}
                              className="d-inline-block mt-1"
                              style={{ cursor: 'pointer' }}
                              title="View delivery feedback"
                              onClick={() =>
                                setSelectedReview(toDeliveryReview(row))
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  setSelectedReview(toDeliveryReview(row));
                                }
                              }}
                            >
                              {renderStars(row.delivery_rating)}
                            </span>
                          )}
                        </td>
                        <td>
                          <p className="fs-12 mb-0">
                            {row?.customer_name || '-'}
                          </p>
                          {row?.customer_contact && (
                            <p className="fs-10 mb-0 mt-1">
                              {row.customer_contact}
                            </p>
                          )}
                        </td>
                        <td>
                          <p className="fs-12 mb-0">{row?.store_code || '-'}</p>
                        </td>
                        <td>
                          <p className="fs-12 mb-0">
                            {row?.dispatch_location || '-'}
                          </p>
                        </td>
                        <td>{row?.total_items ?? 0}</td>
                        <td nowrap="nowrap">
                          {row?.total_order_qty ?? 0} / {row?.total_delivery_qty ?? 0}{' '}
                          / {row?.total_fixing_qty ?? 0}
                        </td>
                        <td nowrap="nowrap">
                          {Number(row?.installation_success_count) > 0 && (
                            <span
                              className="badge bg-soft-success text-success me-1"
                              title={`Installation Success : ${row.installation_success_count}`}
                            >
                              {row.installation_success_count}
                            </span>
                          )}
                          {Number(row?.installation_failed_count) > 0 && (
                            <span
                              className="badge bg-soft-danger text-danger"
                              title={`Installation Failed : ${row.installation_failed_count}`}
                            >
                              {row.installation_failed_count}
                            </span>
                          )}
                          {!Number(row?.installation_success_count) &&
                            !Number(row?.installation_failed_count) &&
                            '-'}
                        </td>
                        <td nowrap="nowrap">
                          {Number(row?.lifestyle_image_count) > 0 && (
                            <span
                              role="button"
                              tabIndex={0}
                              className="badge bg-soft-info text-info me-1"
                              style={{ cursor: 'pointer' }}
                              title="View lifestyle images"
                              onClick={() => openPodImages(row)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  openPodImages(row);
                                }
                              }}
                            >
                              <i className="mdi mdi-image-multiple me-1"></i>
                              {row.lifestyle_image_count}
                            </span>
                          )}
                          {!Number(row?.lifestyle_image_count) && '-'}
                        </td>
                        <td nowrap="nowrap">
                          <Button
                            size="sm"
                            color="dark"
                            outline
                            onClick={() => setSelectedRow(row)}
                            title="View delivery details"
                          >
                            <i className="mdi mdi-eye-outline"></i>
                          </Button>
                          {row?.tracking_link && (
                            <a
                              href={row.tracking_link}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm btn-outline-secondary ms-1"
                              title="Tracking link"
                            >
                              <i className="mdi mdi-map-marker-path"></i>
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan={13}>No Records found</td>
                    </tr>
                  </tbody>
                )}
              </Table>
            </div>
            {listData?.length > 0 ? (
              <DHPagination
                totalPages={recordsTotal}
                currentPage={paginationValue.page}
                setPaginationValue={setPaginationValue}
                setLimitValue={setLimitValue}
              />
            ) : null}
          </div>
        </Col>
      </Row>

      <DriverAnalysisDetailsOffcanvas
        isOpen={!!selectedRow}
        toggle={() => setSelectedRow(null)}
        deliveryData={selectedRow}
      />

      <DeliveryReviewDetailsOffcanvas
        isOpen={!!selectedReview}
        toggle={() => setSelectedReview(null)}
        review={selectedReview}
      />

      <ImagePreviewModal preview={preview} onClose={() => setPreview(null)} />
    </>
  );
};

export default DriverAnalysisList;
